import styled from "styled-components";

export const ShowcaseRoot = styled.div`
  position: relative;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: clamp(12px, 2.5vw, 28px) clamp(10px, 2vw, 24px) clamp(20px, 4vw, 40px);

  @media (max-width: 575.98px) {
    padding: 8px clamp(8px, 3vw, 14px) 16px;
  }
`;

/** Viewport clips the row; track translates so the active card stays centered */
export const FilmstripViewport = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: clamp(20px, 4vw, 40px) 0 clamp(8px, 2vw, 16px);
  --filmstrip-gap: clamp(14px, 2.2vw, 28px);
  touch-action: pan-y pinch-zoom;

  @media (max-width: 767.98px) {
    padding: 12px 0 6px;
    --filmstrip-gap: clamp(10px, 2.5vw, 14px);
    /* pan-y: page scrolls vertically; horizontal goes to our pointer handlers (not browser pan). */
    touch-action: pan-y;
  }
`;

export const FilmstripTrack = styled.div<{ $tx: number; $ms: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--filmstrip-gap);
  width: max-content;
  will-change: transform;
  transform: translate3d(${(p) => p.$tx}px, 0, 0);
  transition: transform
    ${(p) => p.$ms}ms
    cubic-bezier(0.22, 1, 0.36, 1);
`;

export const FilmstripSlide = styled.button<{ $active: boolean }>`
  position: relative;
  flex: 0 0 clamp(200px, 17vw, 272px);
  aspect-ratio: 9 / 16;
  min-height: clamp(360px, 58vh, 720px);
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  border-radius: clamp(14px, 2vw, 22px);
  overflow: hidden;
  background: rgba(15, 23, 42, 0.55);
  -webkit-tap-highlight-color: transparent;

  transform: scale(${(p) => (p.$active ? 1.06 : 0.9)});
  opacity: ${(p) => (p.$active ? 1 : 0.55)};
  filter: ${(p) =>
    p.$active
      ? "brightness(1.08) saturate(1.06)"
      : "brightness(0.78) saturate(0.9)"};
  z-index: ${(p) => (p.$active ? 3 : 1)};
  box-shadow: ${(p) =>
    p.$active
      ? `
    0 28px 72px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.14),
    0 0 56px rgba(99, 102, 241, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12)
  `
      : `
    0 14px 40px rgba(0, 0, 0, 0.38),
    0 0 0 1px rgba(255, 255, 255, 0.06)
  `};

  transition:
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.55s ease,
    filter 0.55s ease,
    box-shadow 0.55s ease;

  &:hover {
    transform: scale(${(p) => (p.$active ? 1.08 : 0.94)});
    opacity: ${(p) => (p.$active ? 1 : 0.7)};
    filter: ${(p) =>
      p.$active
        ? "brightness(1.1) saturate(1.08)"
        : "brightness(0.86) saturate(0.95)"};
  }

  &:focus-visible {
    outline: 2px solid rgba(125, 211, 252, 0.85);
    outline-offset: 4px;
  }

  @media (max-width: 767.98px) {
    flex: 0 0 min(72vw, 300px);
    min-height: clamp(200px, 38vh, 400px);
    aspect-ratio: 3 / 4;
    transform: scale(${(p) => (p.$active ? 1.04 : 0.9)});
    opacity: ${(p) => (p.$active ? 1 : 0.48)};
    filter: ${(p) =>
      p.$active
        ? "brightness(1.1) saturate(1.08)"
        : "brightness(0.72) saturate(0.88)"};
    box-shadow: ${(p) =>
      p.$active
        ? `0 18px 44px rgba(0, 0, 0, 0.48), 0 0 0 2px rgba(125, 211, 252, 0.55), 0 0 28px rgba(99, 102, 241, 0.35)`
        : `0 10px 28px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.06)`};
    z-index: ${(p) => (p.$active ? 4 : 1)};

    &:hover {
      transform: scale(${(p) => (p.$active ? 1.06 : 0.92)});
    }
  }

  @media (max-width: 767.98px) and (hover: none) {
    transform: scale(${(p) => (p.$active ? 1.05 : 0.9)});
    opacity: ${(p) => (p.$active ? 1 : 0.46)};
  }

  @media (max-width: 380px) {
    flex: 0 0 min(78vw, 280px);
    min-height: clamp(180px, 36vh, 340px);
  }
`;

export const SlideImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
`;

export const SlideCaption = styled.span<{ $active: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: clamp(0.65rem, 2vw, 1rem) 0.65rem;
  font-size: clamp(0.62rem, 1.05vw, 0.78rem);
  font-weight: 750;
  letter-spacing: 0.05em;
  line-height: 1.25;
  text-transform: uppercase;
  text-align: center;
  text-wrap: balance;
  color: #f8fafc;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.9);
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(3, 7, 18, ${(p) => (p.$active ? 0.75 : 0.88)}) 100%
  );
  pointer-events: none;

  @media (max-width: 575.98px) {
    font-size: clamp(0.58rem, 2.8vw, 0.72rem);
    padding: 0.5rem 0.45rem;
  }
`;

export const FilmstripHero = styled.div`
  text-align: center;
  padding: clamp(0.5rem, 2vw, 1.25rem) clamp(0.75rem, 3vw, 2rem) 0;
  max-width: 40rem;
  margin: 0 auto;

  @media (max-width: 575.98px) {
    padding: 0.5rem 0.75rem 0;
    max-width: 100%;
  }
`;

export const HeroMeta = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(165, 180, 252, 0.75);

  @media (max-width: 575.98px) {
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    padding: 0 0.25rem;
  }
`;

export const HeroTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: clamp(1.05rem, 2.5vw, 1.75rem);
  font-weight: 750;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  text-wrap: balance;
  color: #f8fafc;
  text-shadow:
    0 2px 24px rgba(0, 0, 0, 0.75),
    0 0 36px rgba(99, 102, 241, 0.2);

  @media (max-width: 575.98px) {
    font-size: clamp(0.95rem, 4.2vw, 1.35rem);
    line-height: 1.2;
    padding: 0 0.35rem;
    hyphens: auto;
    overflow-wrap: anywhere;
  }
`;

export const NavCluster = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: clamp(1rem, 2.5vw, 1.5rem);
  flex-wrap: nowrap;
  width: 100%;
  max-width: 100%;
  padding: 0 4px;
  box-sizing: border-box;

  @media (max-width: 575.98px) {
    gap: 0.35rem;
    margin-top: 0.75rem;
  }
`;

export const NavArrow = styled.button<{ $dir: "prev" | "next" }>`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(129, 140, 248, 0.35);
  background: rgba(15, 23, 42, 0.65);
  color: #e2e8f0;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition:
    transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.3s ease,
    border-color 0.3s ease;

  &:hover:not(:disabled) {
    transform: scale(1.08);
    border-color: rgba(125, 211, 252, 0.55);
    box-shadow: 0 0 28px rgba(99, 102, 241, 0.25);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  @media (max-width: 575.98px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

export const DotTrack = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
  padding: 0.35rem 0.25rem;
  flex: 1 1 auto;
  min-width: 0;
  max-width: min(100%, 320px);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.45) transparent;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.4);
    border-radius: 3px;
  }

  @media (max-width: 575.98px) {
    max-width: min(calc(100vw - 120px), 280px);
    gap: 0.28rem;
  }
`;

export const Dot = styled.button<{ $active: boolean }>`
  width: ${(p) => (p.$active ? "22px" : "7px")};
  height: 7px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  transition:
    width 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.35s ease;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(90deg, #6366f1, #22d3ee)"
      : "rgba(148, 163, 184, 0.35)"};

  &:hover {
    background: ${(p) =>
      p.$active
        ? "linear-gradient(90deg, #6366f1, #22d3ee)"
        : "rgba(148, 163, 184, 0.55)"};
  }
`;
